import './AppTemplate.css'

import { compose, withProps, withState } from 'recompose'
import { Link, withRouter, Switch, Route } from 'react-router-dom'
import React from 'react'
import AppBar from 'material-ui/AppBar'
import Drawer from 'material-ui/Drawer'
import Menu from 'material-ui/Menu'
import MenuItem from 'material-ui/MenuItem'
import muiThemeable from 'material-ui/styles/muiThemeable'
import MenuIcon from 'material-ui/svg-icons/navigation/menu'
import { negate } from 'lodash'

import NewVersionPrompt from './NewVersionPrompt'
import {
  aboutRoute,
  chatRoute,
  indexRoute,
  programRoute,
  reportProblemRoute,
  sponsorsRoute,
  trackRoute,
  venueRoute,
  presentationDetailRoute,
} from '../app/routes'
import {
  getNavigationLabels,
  getTracks,
  getPresentations,
  getAppTitle,
  getNavigationIcons,
  getNavigationItems,
} from '../app/selectors'
import { withSizes } from '../app/utils'
import content from '../content'
import { isTrackCrossTrack } from '../app/predicates'
import { IconButton } from 'material-ui'
import PresentationIcon from '../assets/svg/PresentationIcon'
import InlineIconWrapper from './InlineIconWrapper'
import ProgramIcon from '../assets/svg/ProgramIcon'
import ChatIcon from '../assets/svg/ChatIcon'
import SpeakerIcon from '../assets/svg/SpeakerIcon'
import ContactIcon from '../assets/svg/ContactIcon'
import PartnersIcon from '../assets/svg/PartnersIcon'
import ProblemIcon from '../assets/svg/ProblemIcon'
import AboutIcon from '../assets/svg/AboutIcon'

// disable swipe of drawer as swipe is also used for back (e.g. iOS)
const DISABLE_SWIPE = true

const DummyIcon = () => <div style={{ width: 48 }} />

const Circle = ({ color, full, size = 11, style }) => (
  <div
    style={{
      width: size,
      height: size,
      border: `2px solid ${color}`,
      borderRadius: '50%',
      backgroundColor: full ? color : undefined,
      ...style,
    }}
  />
)

const str = s => () => s
const makeFinder = (array, accessor, defaultValue) => ({ match }) =>
  (array.find(({ id }) => id === match.params.id) || {
    [accessor]: defaultValue,
  })[accessor]
const _Title = ({ labels, presentations, tracks }) => (
  <Switch>
    <Route exact path={programRoute} component={str(labels.program)} />
    <Route path={venueRoute} component={str(labels.venue)} />
    <Route path={aboutRoute} component={str(labels.about)} />
    <Route path={sponsorsRoute} component={str(labels.sponsors)} />
    <Route path={chatRoute} component={str(labels.chat)} />
    <Route
      path={trackRoute(':id')}
      component={makeFinder(tracks, 'label', labels.program)}
    />
    <Route
      path={presentationDetailRoute(':id')}
      component={makeFinder(presentations, 'title', '')}
    />
    <Route path={reportProblemRoute} component={str(labels.reportProblem)} />
  </Switch>
)
const Title = withProps({
  labels: getNavigationLabels(content),
  presentations: getPresentations(content),
  tracks: getTracks(content),
})(_Title)

const titleStyle = {
  cursor: 'pointer',
  fontSize: 18,
}

const ROUTE_MAP = {
  program: programRoute,
  chat: chatRoute,
  venue: venueRoute,
  about: aboutRoute,
  sponsors: sponsorsRoute,
  reportProblem: reportProblemRoute,
}

const ICONS = {
  program: ProgramIcon,
  presentation: PresentationIcon,
  speaker: SpeakerIcon,
  chat: ChatIcon,
  contact: ContactIcon,
  partners: PartnersIcon,
  problem: ProblemIcon,
  about: AboutIcon,
}

const AppTemplate = ({
  isDrawerOpen,
  setDrawerOpen,
  children,
  location,
  isDesktop,
  navigationLabels,
  navigationIcons,
  navigationItems,
  tracks,
  muiTheme,
  history,
  title,
}) => {
  const openDrawer = () => setDrawerOpen(true)
  const closeDrawer = e => {
    setDrawerOpen(false)
    e.stopPropagation()
  }
  const {
    palette: { primary1Color },
    appBar: { height: appBarHeight },
    custom: {
      metrics: { basePadding, maxContentWidth },
      images: { logo },
      colors: { backgroundColor },
    },
    selectedMenuItemStyle,
    menuItemStyle,
  } = muiTheme
  const contentStyle = {
    margin: '0 auto',
    padding: basePadding,
    paddingTop: appBarHeight + basePadding,
    maxWidth: maxContentWidth,
  }
  const navigateHome = () => history.push(indexRoute)
  return (
    <div className="AppTemplate-container" style={{ backgroundColor }}>
      <AppBar
        className="AppTemplate-appbar"
        title={<Title />}
        onTitleClick={navigateHome}
        titleStyle={titleStyle}
        onLeftIconButtonClick={openDrawer}
        style={{ position: 'fixed', textAlign: 'center' }}
        iconElementRight={<DummyIcon />}
      />
      <div style={{ margin: isDesktop ? '0 0 0 260px' : '0' }}>
        <div className="AppTemplate-content" style={contentStyle}>
          {children}
        </div>
      </div>
      <Drawer
        open={isDesktop || isDrawerOpen}
        docked={isDesktop}
        containerStyle={{ overflow: 'hidden' }}
        onRequestChange={open => setDrawerOpen(open)}
        disableSwipeToOpen={DISABLE_SWIPE}
      >
        <div
          className="AppTemplate-drawerLogo"
          style={{
            backgroundImage: `url(${logo})`,
            ...titleStyle,
          }}
          onClick={navigateHome}
        >
          {!isDesktop && (
            <IconButton onClick={closeDrawer}>
              <MenuIcon color={primary1Color} />
            </IconButton>
          )}
        </div>
        <Menu
          value={location.pathname}
          onItemClick={closeDrawer}
          selectedMenuItemStyle={selectedMenuItemStyle}
        >
          {navigationItems.map(item => {
            const route = ROUTE_MAP[item]
            if (!route) {
              return null
            }

            const label = navigationLabels[item]
            const Icon = ICONS[navigationIcons[item]]

            const menuItem = (
              <MenuItem
                style={menuItemStyle}
                innerDivStyle={{ paddingLeft: 10 }}
                key={item}
                value={route}
                containerElement={<Link to={route} />}
                primaryText={
                  Icon ? (
                    <InlineIconWrapper iconComponent={Icon}>
                      {label}
                    </InlineIconWrapper>
                  ) : (
                    label
                  )
                }
              />
            )

            return item === 'program'
              ? [
                  menuItem,
                  ...tracks
                    .filter(negate(isTrackCrossTrack))
                    .map(({ id, label, color }) => {
                      const tRoute = trackRoute(id)
                      const Icon = ICONS[navigationIcons.track]
                      const circle = (
                        <Circle
                          key="circle"
                          // Workaround for a lack of knowledge about selected state
                          // If there is a better way, replace this
                          full={tRoute === location.pathname}
                          color={color}
                          size={11}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            marginTop: -5.5,
                            right: 39,
                          }}
                        />
                      )
                      return (
                        <MenuItem
                          key={id}
                          style={menuItemStyle}
                          innerDivStyle={{ paddingLeft: 30 }}
                          value={tRoute}
                          containerElement={<Link to={tRoute} />}
                          primaryText={
                            Icon
                              ? [
                                  <InlineIconWrapper
                                    key="icon"
                                    iconComponent={Icon}
                                  >
                                    {label}
                                  </InlineIconWrapper>,
                                  circle,
                                ]
                              : [label, circle]
                          }
                        />
                      )
                    }),
                ]
              : menuItem
          })}
        </Menu>
      </Drawer>
      <NewVersionPrompt />
    </div>
  )
}

const withDrawerState = withState('isDrawerOpen', 'setDrawerOpen', false)
const withPageContent = withProps({
  navigationLabels: getNavigationLabels(content),
  navigationIcons: getNavigationIcons(content),
  navigationItems: getNavigationItems(content),
  tracks: getTracks(content),
  title: getAppTitle(content),
})

export default compose(
  withRouter,
  withDrawerState,
  withPageContent,
  withSizes(),
  muiThemeable()
)(AppTemplate)



// WEBPACK FOOTER //
// ./src/components/AppTemplate.js